import React, { useEffect, useState, forwardRef } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDateForInput, formatDateTimeForInput } from '../../../functions/formatDateTime';
import Icon from '../../../icons/Icon';
import DatePickerCustomHeader from './helpers/DatePickerCustomHeader';

const DateField = forwardRef(({
  fieldName,
  wrapperClass = '',
  label,
  hideLabel = false,
  allowNull = true,
  inputType = 'text',
  value = '',
  step,
  errors = [],
  helpText = '',
  parent = '',
  autofocus = false,
  disabled = false,
  options = null,
  width = '',
  style = {},
  many = false,
  onChange = () =>  {},
  pk = null
}, ref) => {
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Convert value to Date object for DatePicker
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setSelectedDate(isNaN(date.getTime()) ? null : date);
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    
    // Format the date for the onChange callback
    let formattedValue = '';
    if (date) {
      formattedValue = formatDateForInput(date);
    }
    onChange({
      'name': fieldName, 
      'value': formattedValue, 
      'label': formattedValue
    });
  };

  const optionDayKey = (option) => {
    const raw = option?.id ?? option?.label ?? option?.value ?? option?.date;
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      const ms = raw < 1e12 ? raw * 1000 : raw;
      return formatDateForInput(new Date(ms));
    }
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : formatDateForInput(parsed);
  };

  const filterAllowedDates = (date) => {
    if (!options || !Array.isArray(options) || options.length === 0) {
      return true;
    }
    const day = formatDateForInput(date);
    return options.some(
      (option) => !option.disabled && optionDayKey(option) === day
    );
  };

  const icon = <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M14 22H10C6.22876 22 4.34315 22 3.17157 20.8284C2 19.6569 2 17.7712 2 14V12C2 8.22876 2 6.34315 3.17157 5.17157C4.34315 4 6.22876 4 10 4H14C17.7712 4 19.6569 4 20.8284 5.17157C22 6.34315 22 8.22876 22 12V14C22 17.7712 22 19.6569 20.8284 20.8284C20.1752 21.4816 19.3001 21.7706 18 21.8985" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path> <path d="M7 4V2.5" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path> <path d="M17 4V2.5" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path> <path d="M21.5 9H16.625H10.75M2 9H5.875" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path> <path d="M18 17C18 17.5523 17.5523 18 17 18C16.4477 18 16 17.5523 16 17C16 16.4477 16.4477 16 17 16C17.5523 16 18 16.4477 18 17Z" fill="#1C274C"></path> <path d="M18 13C18 13.5523 17.5523 14 17 14C16.4477 14 16 13.5523 16 13C16 12.4477 16.4477 12 17 12C17.5523 12 18 12.4477 18 13Z" fill="#1C274C"></path> <path d="M13 17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17C11 16.4477 11.4477 16 12 16C12.5523 16 13 16.4477 13 17Z" fill="#1C274C"></path> <path d="M13 13C13 13.5523 12.5523 14 12 14C11.4477 14 11 13.5523 11 13C11 12.4477 11.4477 12 12 12C12.5523 12 13 12.4477 13 13Z" fill="#1C274C"></path> <path d="M8 17C8 17.5523 7.55228 18 7 18C6.44772 18 6 17.5523 6 17C6 16.4477 6.44772 16 7 16C7.55228 16 8 16.4477 8 17Z" fill="#1C274C"></path> <path d="M8 13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13C6 12.4477 6.44772 12 7 12C7.55228 12 8 12.4477 8 13Z" fill="#1C274C"></path> </g></svg>
  const isMobile = window?.innerWidth <= 980
  const commonDatePickerProps = {
    className: "form-control",
    calendarClassName: "react-datepicker-custom",
    wrapperClassName: "w-100",
    isClearable: false,
    monthsShown: isMobile ? 1 : 2,
    renderCustomHeader: DatePickerCustomHeader,
    timeIntervals: 15,
    popperPlacement: isMobile ? "bottom-start" : "bottom"
};
  return <>
   <DatePicker
      {...commonDatePickerProps}
      ref={ref}
      showIcon
      icon={icon}
      id={pk ? pk + '_' + fieldName : fieldName}
      selected={selectedDate}
      onChange={handleDateChange}
      dateFormat="d MMMM, yyyy"
      className={`d-block ${errors.length > 0 ? 'is-invalid' : ''}`}
      placeholderText={style.placeholder || "Select date..."}
      disabled={disabled}
      autoFocus={autofocus}
      isClearable={allowNull}
      showPopperArrow={false}
      filterDate={filterAllowedDates}
      popperPlacement="bottom-start"
      customInput={
        <input
          className={`form-control ${errors.length > 0 ? 'is-invalid' : ''}`}
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? '#e9ecef' : 'white',
            ...style
          }}
        />
      }
    />
{/*       
      {helpText && (
        <div className="form-text">{helpText}</div>
      )}
      
      {errors.length > 0 && (
        <div className="invalid-feedback">
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )} */}
    </>
});

export default DateField;